import React, { useState, useEffect } from 'react';
import { ThemeColors } from '../../../../Context/ThemeContext';
import type { CustomDateRange } from '../types';
import { DateRangePicker } from './DateRangePicker';
import { dashboardHandler, createCancelSource, isRequestCancelled } from '../DashboardHandler';

interface RevenueProduct {
  productName: string;
  productType: string;
  unitsSold: number;
  amount: number;
}

interface RevenueData {
  totalEarned: number;
  completedCount: number;
  startDate?: string | null;
  endDate?: string | null;
  products: RevenueProduct[];
  boardAdsWatched: number;
  hintAdsWatched: number;
  moreTimeAdsWatched: number;
}

interface RevenueCardProps {
  isMobile: boolean;
  theme: ThemeColors;
}

const formatCurrency = (n: number): string =>
  (n ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const formatDay = (iso: string): string => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const RevenueCard: React.FC<RevenueCardProps> = ({ isMobile, theme }) => {
  const [customRange, setCustomRange] = useState<CustomDateRange | undefined>(undefined);
  const [data, setData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(false);

  useEffect(() => {
    const source = createCancelSource();
    setLoading(true);
    setError(null);
    const customDate = customRange
      ? { startDate: customRange.startDate, endDate: customRange.endDate }
      : undefined;
    dashboardHandler
      .getRevenue(source.token, customDate)
      .then((res: RevenueData) => {
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        if (isRequestCancelled(err)) return;
        setError('Failed to load revenue');
        setLoading(false);
      });
    return () => source.cancel();
  }, [customRange]);

  const rangeLabel = customRange
    ? `${formatDay(customRange.startDate)} – ${formatDay(customRange.endDate)}`
    : 'All time';

  const totalAds =
    (data?.boardAdsWatched ?? 0) + (data?.hintAdsWatched ?? 0) + (data?.moreTimeAdsWatched ?? 0);

  const styles: Record<string, React.CSSProperties> = {
    revenueCard: {
      position: 'relative',
      background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
      borderRadius: '8px',
      padding: '20px',
      color: '#fff',
      boxShadow: '0 4px 12px rgba(34,197,94,0.3)',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    label: { fontSize: '15px', fontWeight: 500, opacity: 0.95 },
    total: { fontSize: isMobile ? '26px' : '32px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.1 },
    sub: { fontSize: '12px', opacity: 0.9 },
    adRow: { fontSize: '12px', opacity: 0.95, marginTop: '6px' },
    breakdownBtn: {
      marginTop: '12px',
      alignSelf: 'flex-start',
      background: 'rgba(255,255,255,0.18)',
      color: '#fff',
      border: 'none',
      borderRadius: '6px',
      padding: '6px 12px',
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
    },
    overlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.6)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
    },
    modalCard: {
      background: theme.card,
      borderRadius: '12px',
      padding: '20px',
      width: '100%',
      maxWidth: '480px',
      maxHeight: '80vh',
      overflowY: 'auto',
      boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
    },
    modalHeader: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', marginBottom: '4px' },
    modalTitle: { fontSize: '16px', fontWeight: 700, color: theme.text },
    modalRange: { fontSize: '12px', color: theme.textSecondary },
    modalTotal: { fontSize: '22px', fontWeight: 700, color: '#22c55e', marginBottom: '14px' },
    modalTotalSub: { fontSize: '12px', fontWeight: 500, color: theme.textSecondary, marginLeft: '8px' },
    prodRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      padding: '10px 0',
      borderBottom: `1px solid ${theme.border}`,
    },
    prodName: { fontSize: '14px', fontWeight: 500, color: theme.text },
    prodMeta: { fontSize: '11px', color: theme.textSecondary, marginTop: '2px' },
    prodAmount: { fontSize: '14px', fontWeight: 600, color: theme.text, whiteSpace: 'nowrap' },
    empty: { fontSize: '13px', color: theme.textSecondary, padding: '12px 0' },
    adsSection: { marginTop: '16px', padding: '12px', background: theme.inputBg, borderRadius: '8px' },
    adsTitle: { fontSize: '12px', fontWeight: 600, color: theme.textSecondary, marginBottom: '8px' },
    adsGrid: { display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: theme.text },
    footnote: { fontSize: '11px', color: theme.textSecondary, fontStyle: 'italic', marginTop: '14px' },
    closeBtn: {
      marginTop: '16px',
      width: '100%',
      padding: '10px',
      background: '#3b82f6',
      color: '#fff',
      border: 'none',
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: 600,
      cursor: 'pointer',
    },
  };

  return (
    <div style={styles.revenueCard}>
      <DateRangePicker
        value={customRange}
        onChange={setCustomRange}
        theme={theme}
        isActive={!!customRange}
        style={{ top: '14px', right: '14px' }}
        popoverAlign="right"
      />

      <div style={styles.label}>Revenue</div>
      <div style={styles.total}>{loading ? '…' : error ? '—' : formatCurrency(data?.totalEarned ?? 0)}</div>
      <div style={styles.sub}>
        {rangeLabel}
        {!loading && !error ? ` · ${data?.completedCount ?? 0} purchases (gross)` : ''}
      </div>

      <div style={styles.adRow}>
        Ad views — Board {data?.boardAdsWatched ?? 0} · Hint {data?.hintAdsWatched ?? 0} · More Time{' '}
        {data?.moreTimeAdsWatched ?? 0}
      </div>

      <button
        style={styles.breakdownBtn}
        onClick={() => setShowBreakdown(true)}
        disabled={loading || !!error}
      >
        View breakdown
      </button>

      {showBreakdown && (
        <div style={styles.overlay} onClick={() => setShowBreakdown(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <span style={styles.modalTitle}>Revenue breakdown</span>
              <span style={styles.modalRange}>{rangeLabel}</span>
            </div>
            <div style={styles.modalTotal}>
              {formatCurrency(data?.totalEarned ?? 0)}
              <span style={styles.modalTotalSub}>gross · {data?.completedCount ?? 0} purchases</span>
            </div>

            {data && data.products.length > 0 ? (
              data.products.map((p) => (
                <div key={`${p.productName}-${p.productType}`} style={styles.prodRow}>
                  <div>
                    <div style={styles.prodName}>{p.productName}</div>
                    <div style={styles.prodMeta}>
                      {p.productType} · {p.unitsSold} sold
                    </div>
                  </div>
                  <div style={styles.prodAmount}>{formatCurrency(p.amount)}</div>
                </div>
              ))
            ) : (
              <div style={styles.empty}>No purchases in this range.</div>
            )}

            <div style={styles.adsSection}>
              <div style={styles.adsTitle}>Ad views in range (no revenue value) — {totalAds} total</div>
              <div style={styles.adsGrid}>
                <div>Board: {data?.boardAdsWatched ?? 0}</div>
                <div>Hint: {data?.hintAdsWatched ?? 0}</div>
                <div>More Time: {data?.moreTimeAdsWatched ?? 0}</div>
              </div>
            </div>

            <div style={styles.footnote}>
              Gross purchase revenue — excludes refunds and store auto-renewals (not recorded in the
              database). Ad views are counts only and carry no monetary value.
            </div>

            <button style={styles.closeBtn} onClick={() => setShowBreakdown(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
